/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* Licensed under the MIT License. See LICENSE.md in the project root for license terms.
*--------------------------------------------------------------------------------------------*/

import { editor, Uri } from "monaco-editor/esm/vs/editor/editor.api";
import { IAnnotation, IAnnotationUpdateData } from "../../types/Annotation";
import { generateUuid } from "./Uuid";

export class Annotation implements IAnnotation {
  private _id: string;
  private _options: editor.IModelDecorationOptions;
  private _uri: Uri;
  private _startLineNumber: number;
  private _endLineNumber: number;
  private _startColumnNumber?: number;
  private _endColumnNumber?: number;


  public constructor(options: editor.IModelDecorationOptions, uri: Uri, startLineNumber: number, endLineNumber: number, startColumnNumber?: number, endColumnNumber?: number) {
    this._id = generateUuid();
    this._options = options;
    this._uri = uri;
    this._startLineNumber = startLineNumber;
    this._endLineNumber = endLineNumber;
    this._startColumnNumber = startColumnNumber;
    this._endColumnNumber = endColumnNumber;
  }

  public get id(): string {
    return this._id;
  }

  public get options(): editor.IModelDecorationOptions {
    return this._options;
  }

  public get lineNumber(): number {
    return this._startLineNumber;
  }

  public get uri(): Uri {
    return this._uri;
  }

  public get column(): number | undefined {
    return this._startColumnNumber;
  }

  public get endLineNumber(): number {
    return this._endLineNumber;
  }

  public get endColumn(): number | undefined {
    return this._endColumnNumber;
  }

  public update(data: IAnnotationUpdateData): void {
    if (!isUndefinedOrNull(data.lineNumber)) {
      this._startLineNumber = data.lineNumber;
    }
    if (!isUndefinedOrNull(data.column)) {
      this._startColumnNumber = data.column;
    }
    if (!isUndefinedOrNull(data.endLineNumber)) {
      this._endLineNumber = data.endLineNumber;
    }
    if (!isUndefinedOrNull(data.endColumn)) {
      this._endColumnNumber = data.endColumn;
    }
  }
}

function isUndefinedOrNull(value: unknown): value is undefined {
  return value === undefined || value === null;
}